import { useSearchParams } from "react-router-dom";
import { Close } from "@dreamhub/designsystem/icons";
import toast from "react-hot-toast";
import IntegrationsWrapper from "./components/IntegrationsWrapper";
import { AccountData } from "@/entities/accounts/types";
import { UserData } from "@/entities/users/types";
import { IconButton } from "@/components/ui/icon-button";
import ToastBody from "@/components/ui/toast-body";

const VALID_INTEGRATIONS = [
  "hubspot",
  "salesforce",
  "pardot",
  "zendesk",
  "apollo",
  "pipedrive",
  "fathom"
] as const;
type IntegrationType = (typeof VALID_INTEGRATIONS)[number];

const isValidIntegration = (name: string | null): name is IntegrationType => {
  return VALID_INTEGRATIONS.includes(name as IntegrationType);
};

const INTEGRATION_TOAST_TIMEOUT = 3000;

interface Props {
  userData: UserData;
  account: AccountData;
}

const Integrations: React.FC<Props> = (props) => {
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name");
  const expertMode = searchParams.get("expert") === "true";

  // Validate and show error if needed
  if (!name) {
    toast.error(
      <ToastBody
        title="Integration name is required. Please provide a 'name' query parameter."
        actions={
          <IconButton
            size="md"
            variant="iconNoBg"
            iconColor="secondary"
            onClick={() => toast.dismiss()}
          >
            <Close />
          </IconButton>
        }
      />,
      {
        id: "integration-name-missing",
        icon: null,
        duration: INTEGRATION_TOAST_TIMEOUT
      }
    );
    return null;
  }

  if (!isValidIntegration(name)) {
    toast.error(
      <ToastBody
        title={`Unknown integration: ${name}.`}
        actions={
          <IconButton
            size="md"
            variant="iconNoBg"
            iconColor="secondary"
            onClick={() => toast.dismiss()}
          >
            <Close />
          </IconButton>
        }
      />,
      {
        id: "integration-invalid",
        icon: null,
        duration: INTEGRATION_TOAST_TIMEOUT
      }
    );
    return null;
  }

  const handleInstallSuccess = (
    _installationId: string,
    _configObject: unknown
  ) => {
    toast.success(
      <ToastBody title={`${name} integration installed successfully`} />,
      { duration: INTEGRATION_TOAST_TIMEOUT }
    );
  };

  const handleUpdateSuccess = (
    _installationId: string,
    _configObject: unknown
  ) => {
    toast.success(
      <ToastBody title={`${name} integration updated successfully`} />,
      { duration: INTEGRATION_TOAST_TIMEOUT }
    );
  };

  const handleUninstallSuccess = (_installationId: string) => {
    toast.success(
      <ToastBody title={`${name} integration uninstalled successfully`} />,
      { duration: INTEGRATION_TOAST_TIMEOUT }
    );
  };

  return (
    <div data-test="integrations-page">
      <IntegrationsWrapper
        data-test={`integrations-wrapper-${name}`}
        {...props}
        integrationName={name}
        expertMode={expertMode}
        onInstallSuccess={handleInstallSuccess}
        onUpdateSuccess={handleUpdateSuccess}
        onUninstallSuccess={handleUninstallSuccess}
      />
    </div>
  );
};

export default Integrations;
